import React from 'react';
import { useDroppable, useDraggable } from '@dnd-kit/core';
import { FlyerSlot } from '../../types';
import { X, Image as ImageIcon } from 'lucide-react';
import { ProductFlyerLayout } from '../product/ProductFlyerLayout';

interface DroppableSlotProps {
  id: string;
  slot: FlyerSlot;
  onRemove?: () => void;
  index: number;
  dragId?: string; // je-li nastaveno, obsah slotu lze táhnout v rámci letáku (přesun/výměna)
}

export const DroppableSlot: React.FC<DroppableSlotProps> = ({ id, slot, onRemove, index, dragId }) => {
  const isEmpty = !slot || slot.type === 'empty';

  // Drop povolen i na obsazené sloty (kvůli výměně). Panel→obsazeno řeší logika v editoru.
  const { isOver, setNodeRef: setDropRef } = useDroppable({
    id,
    data: { index },
  });

  const { attributes, listeners, setNodeRef: setDragRef, isDragging } = useDraggable({
    id: dragId ?? `nodrag-${id}`,
    data: { index },
    disabled: !dragId,
  });

  // Vlastnosti pro tažení obsahu (produkt / 1×1 promo).
  const dragProps: any = dragId ? { ref: setDragRef, ...listeners, ...attributes } : {};

  const getPromoImageUrl = (promoId: string) => {
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
    return `${API_URL}/promo-images/${promoId}/image`;
  };
  const product = slot?.product;
  const promoImage = slot?.promoImage;

  return (
    <div
      ref={setDropRef}
      className={`border-2 border-dashed rounded-lg p-1 h-full transition-colors ${
        !isEmpty
          ? 'border-green-300 bg-white'
          : isOver
          ? 'border-blue-500 bg-blue-50'
          : 'border-gray-300 bg-gray-50'
      } ${isDragging ? 'opacity-40' : ''}`}
    >
      {slot?.type === 'product' && product ? (
        // focus:outline-none potlačí matoucí černý rámeček (browser focus-visible outline),
        // který dnd-kit draggable ukáže po výběru + stisku klávesy. DnD zůstává funkční
        // (běží na pointer listenerech; tabIndex/role z atributů zůstávají).
        <div className={`relative h-full focus:outline-none focus-visible:outline-none ${dragId ? 'cursor-move' : ''}`} {...dragProps}>
          {onRemove && (
            <button
              onPointerDown={(e) => e.stopPropagation()}
              onClick={onRemove}
              className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 z-10"
            >
              <X className="w-3 h-3" />
            </button>
          )}
          <ProductFlyerLayout product={product} brandColor={product.brandColor} />
        </div>
      ) : slot?.type === 'promo' && promoImage ? (
        // Viz výše: potlačit matoucí černý focus rámeček, DnD ponechat funkční.
        <div className={`relative h-full focus:outline-none focus-visible:outline-none ${dragId ? 'cursor-move' : ''}`} {...dragProps}>
          {onRemove && (
            <button
              onPointerDown={(e) => e.stopPropagation()}
              onClick={onRemove}
              className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 z-10"
            >
              <X className="w-3 h-3" />
            </button>
          )}

          <img
            src={getPromoImageUrl(promoImage.id)}
            alt={promoImage.name}
            className="w-full h-full object-contain rounded"
            onError={(e) => {
              e.currentTarget.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="180"><rect width="100" height="180" fill="%23E5E7EB"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="%239CA3AF" font-size="12">No Image</text></svg>';
            }}
          />
        </div>
      ) : (
        <div className="h-full flex flex-col items-center justify-center text-gray-400">
          <ImageIcon className="w-8 h-8 mb-2" />
          <span className="text-sm text-center">Přetáhněte sem produkt</span>
        </div>
      )}
    </div>
  );
};
